import { codeableConcept } from '@seen/core/codeableConcept'
import type { CodingAnswerOption } from '@seen/core/codings'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { Button, cn } from '@seen/ui'
import { IconChevronDown } from '@tabler/icons-react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { ObservationAnswerPicker } from '#src/components/rich-text-editor/ObservationNode/ObservationAnswerPicker.tsx'
import { answerTriggerClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { ObservationLastChip } from '#src/components/rich-text-editor/ObservationNode/ObservationHistory.tsx'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { ObservationNoteSlot } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteSlot.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import { CHOOSE_PARTICIPANT } from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import { ObservationNoValue } from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import type {
  ObservationCaptureViewProps,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useObservationHistory } from '#src/hooks/useObservationHistory.ts'

export function ObservationNodeChoiceInput({
  capture,
  ...props
}: ObservationCaptureViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  invariant(
    attributes.scaleType === 'Ord' || attributes.scaleType === 'Nom',
    'scaleType must be Ord or Nom'
  )

  const { participant, noParticipant } = capture

  const practitioner = useCurrentPractitioner()
  const { create } = useNodeRecord()

  const isReadOnly = useIsReadOnly()
  // Answered only where the note can be changed: not in a signed or
  // posted note, nor for a read-only role.
  const locked = isReadOnly || noParticipant || !capture.editable
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)

  const [lastObservation] = useObservationHistory({
    participant: inlineCapture ? participant : undefined,
    coding: { system: attributes.system, code: attributes.code },
    limit: 1,
  })

  // The last answer, offered again as one click, when it is one this field
  // can record: among its answers, or from its value set.
  const repeatAnswer = repeatableAnswer(
    lastObservation,
    attributes.answerOptions ?? []
  )

  const [loading, setLoading] = useState(false)

  const [note, setNote] = useState('')

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (attributes.autofocus && buttonRef.current) {
      buttonRef.current.focus()
      props.updateAttributes({ autofocus: false })
    }
  }, [attributes.autofocus, props])

  const handleSelect = async (
    answerOption: CodingAnswerOption
  ): Promise<void> => {
    setLoading(true)
    await create(
      ExtendedObservation.fromValueCodeableConcept({
        valueCodeableConcept: codeableConcept(answerOption),
        code: codeableConcept({
          code: attributes.code,
          system: attributes.system,
          display: attributes.codingDisplay,
        }),
        participant,
        practitioner: practitioner.reference,
        note,
        status: capture.recordStatus,
      })
    )
    setLoading(false)
  }

  if (!props.editor.isEditable) {
    return <ObservationNoValue label={attributes.codingDisplay} />
  }

  return (
    <ObservationNodeFrame
      attributes={attributes}
      updateAttributes={props.updateAttributes}
      blockedReason={noParticipant ? CHOOSE_PARTICIPANT : undefined}
      participant={participant}
    >
      <ObservationAnswerPicker
        answerOptions={attributes.answerOptions ?? []}
        answerValueSet={attributes.answerValueSet ?? null}
        scaleType={attributes.scaleType}
        codingDisplay={attributes.codingDisplay}
        onSelect={handleSelect}
        // Read-only access keeps the one disabled control it has always had.
        sideBySide={inlineCapture && !isReadOnly}
        disabled={loading || locked}
        firstAnswerRef={buttonRef}
      >
        <Button
          ref={buttonRef}
          variant="unstyled"
          size="unstyled"
          disabled={loading || locked}
          className={cn(
            answerTriggerClass(),
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950 disabled:cursor-not-allowed disabled:opacity-60'
          )}
        >
          Please select one...
          <IconChevronDown className="size-4 shrink-0" />
        </Button>
      </ObservationAnswerPicker>
      <ObservationLastChip
        observation={lastObservation}
        onRepeat={
          repeatAnswer && !locked
            ? () => void handleSelect(repeatAnswer)
            : undefined
        }
        disabled={loading || locked}
      />
      <ObservationNoteSlot note={note}>
        &nbsp;&nbsp;
        <div
          className="mr-[0.1875rem]" // Accounts for the shift below.
        />
        <ObservationRichTextEditor
          placeholder="Notes…"
          disabled={locked}
          singleLine={inlineCapture}
          onChange={setNote}
          className={cn(
            'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
            '-ml-[0.1875rem]' // For visual alignment when the content goes onto multiple lines.
          )}
        />
      </ObservationNoteSlot>
    </ObservationNodeFrame>
  )
}

function repeatableAnswer(
  observation: ExtendedObservation | undefined,
  answerOptions: CodingAnswerOption[]
): CodingAnswerOption | undefined {
  const concept = observation?.valueCodeableConcept
  const coding = concept?.coding?.find((candidate) => candidate.code)
  if (!coding?.code) {
    return undefined
  }
  if (answerOptions.length > 0) {
    return answerOptions.find(
      (option) =>
        option.code === coding.code &&
        (!option.system || !coding.system || option.system === coding.system)
    )
  }
  const option: CodingAnswerOption = {
    code: coding.code,
    display: concept?.text ?? coding.display ?? coding.code,
  }
  if (coding.system) {
    option.system = coding.system
  }
  return option
}
